// Quality monitor — docs/01 §5, docs/06 §3. Samples getStats() every 2 s,
// distills an RTCStatsReport into a StatsSample, drives the degradation
// ladder, and raises the two RTP-inactivity events the state machine consumes:
//   - rtpStalled  (soft, 15 s no inbound media → machine reconnects/restarts)
//   - rtpTimeout  (hard, RTP_TIMEOUT_MS without media AND a restart was
//                  attempted → machine concedes the call; docs/02 §4)

import { TIMEOUTS } from '@merkury/protocol';
import type { TimerHost } from '../types.js';
import {
  QualityLadder,
  SAMPLE_INTERVAL_MS,
  type LadderDecision,
  type StatsSample,
} from './ladder.js';

/** Soft stall: trigger an ICE restart well before the hard timeout. */
export const RTP_SOFT_STALL_MS = 15_000;

/** Minimal shape of one entry of an RTCStatsReport (browser-agnostic). */
export type StatsEntry = Record<string, unknown>;

export interface QualityMonitorDeps {
  /** `pc.getStats()` adapter; null when the peer connection is gone. */
  getStats: () => Promise<Iterable<StatsEntry> | null>;
  timers: TimerHost;
  onDecision: (d: LadderDecision, s: StatsSample) => void;
  onRtpStalled: () => void;
  onRtpTimeout: () => void;
}

export class QualityMonitor {
  private readonly ladder = new QualityLadder();
  private interval: unknown = null;
  private prevInbound: { packetsReceived: number; packetsLost: number } | null = null;
  private lastPacketsSeen: number | null = null;
  private lastProgressAt: number | null = null;
  private restartAttemptedSinceProgress = false;
  private softStallFired = false;
  private hardTimeoutFired = false;

  constructor(private readonly deps: QualityMonitorDeps) {}

  start(): void {
    if (this.interval !== null) return;
    this.interval = this.deps.timers.setInterval(() => {
      void this.sampleOnce();
    }, SAMPLE_INTERVAL_MS);
  }

  stop(): void {
    if (this.interval !== null) {
      this.deps.timers.clearInterval(this.interval);
      this.interval = null;
    }
    this.ladder.reset();
    this.prevInbound = null;
    this.lastPacketsSeen = null;
    this.lastProgressAt = null;
    this.restartAttemptedSinceProgress = false;
    this.softStallFired = false;
    this.hardTimeoutFired = false;
  }

  /** The session reports every restartIce() so the hard timeout can arm. */
  noteIceRestart(): void {
    this.restartAttemptedSinceProgress = true;
  }

  get rung(): number {
    return this.ladder.rung;
  }

  /** Exposed for tests: run one sampling tick with a pre-parsed sample. */
  ingest(sample: StatsSample): LadderDecision {
    // --- RTP inactivity bookkeeping ----------------------------------------
    if (this.lastProgressAt === null) this.lastProgressAt = sample.at;
    const progressed =
      this.lastPacketsSeen === null || sample.packetsReceived > this.lastPacketsSeen;
    this.lastPacketsSeen = sample.packetsReceived;
    if (progressed) {
      this.lastProgressAt = sample.at;
      this.restartAttemptedSinceProgress = false;
      this.softStallFired = false;
      this.hardTimeoutFired = false;
    }
    const stalledMs = sample.at - this.lastProgressAt;
    if (stalledMs >= RTP_SOFT_STALL_MS && !this.softStallFired) {
      this.softStallFired = true;
      this.deps.onRtpStalled();
    }
    if (
      stalledMs >= TIMEOUTS.RTP_TIMEOUT_MS &&
      this.restartAttemptedSinceProgress &&
      !this.hardTimeoutFired
    ) {
      this.hardTimeoutFired = true;
      this.deps.onRtpTimeout();
    }

    const decision = this.ladder.evaluate(sample);
    this.deps.onDecision(decision, sample);
    return decision;
  }

  private async sampleOnce(): Promise<void> {
    const report = await this.deps.getStats();
    if (!report) return;
    const sample = this.parseReport(report, this.deps.timers.now());
    this.ingest(sample);
  }

  /**
   * Distill an RTCStatsReport into a StatsSample. Window loss is computed
   * from packetsLost/packetsReceived deltas between samples; RTT comes from
   * the selected candidate pair (fallback remote-inbound), jitter from
   * inbound-rtp.
   */
  parseReport(report: Iterable<StatsEntry>, at: number): StatsSample {
    let inbound: StatsEntry | null = null;
    let remoteInbound: StatsEntry | null = null;
    let selectedPair: StatsEntry | null = null;
    const byId = new Map<string, StatsEntry>();
    const pairs: StatsEntry[] = [];
    let transportStat: StatsEntry | null = null;

    for (const entry of report) {
      const id = entry['id'];
      if (typeof id === 'string') byId.set(id, entry);
      switch (entry['type']) {
        case 'inbound-rtp':
          if (entry['kind'] === 'audio' || entry['kind'] === undefined) inbound = entry;
          break;
        case 'remote-inbound-rtp':
          if (entry['kind'] === 'audio' || entry['kind'] === undefined) remoteInbound = entry;
          break;
        case 'candidate-pair':
          pairs.push(entry);
          break;
        case 'transport':
          transportStat = entry;
          break;
      }
    }

    const selectedId = transportStat?.['selectedCandidatePairId'];
    if (typeof selectedId === 'string') selectedPair = byId.get(selectedId) ?? null;
    if (!selectedPair) {
      selectedPair =
        pairs.find((p) => p['selected'] === true) ??
        pairs.find((p) => p['nominated'] === true && p['state'] === 'succeeded') ??
        null;
    }

    // --- loss over the window ----------------------------------------------
    const packetsReceived = num(inbound?.['packetsReceived']) ?? 0;
    const packetsLost = num(inbound?.['packetsLost']) ?? 0;
    let fractionLost: number | null = null;
    if (this.prevInbound) {
      const dRecv = packetsReceived - this.prevInbound.packetsReceived;
      const dLost = packetsLost - this.prevInbound.packetsLost;
      const total = dRecv + Math.max(0, dLost);
      if (total > 0) fractionLost = Math.max(0, dLost) / total;
      else fractionLost = null; // no packets at all — stall logic handles it
    }
    this.prevInbound = { packetsReceived, packetsLost };

    // --- latency signals ----------------------------------------------------
    const pairRtt = num(selectedPair?.['currentRoundTripTime']);
    const remoteRtt = num(remoteInbound?.['roundTripTime']);
    const rttSeconds = pairRtt ?? remoteRtt;
    const jitterSeconds = num(inbound?.['jitter']) ?? num(remoteInbound?.['jitter']);
    // jitterBufferDelay is a cumulative sum (seconds); current delay is the
    // per-emitted average — good enough as a latency signal.
    const jbDelay = num(inbound?.['jitterBufferDelay']);
    const jbCount = num(inbound?.['jitterBufferEmittedCount']);
    const jitterBufferDelayMs =
      jbDelay !== null && jbCount !== null && jbCount > 0 ? (jbDelay / jbCount) * 1000 : null;

    // --- path type -----------------------------------------------------------
    let usingRelay = false;
    let transport: StatsSample['transport'] = null;
    const localId = selectedPair?.['localCandidateId'];
    if (typeof localId === 'string') {
      const local = byId.get(localId);
      if (local) {
        usingRelay = local['candidateType'] === 'relay';
        const proto = local['relayProtocol'] ?? local['protocol'];
        if (proto === 'udp' || proto === 'tcp' || proto === 'tls') transport = proto;
      }
    }

    return {
      at,
      rttMs: rttSeconds !== null ? rttSeconds * 1000 : null,
      jitterMs: jitterSeconds !== null ? jitterSeconds * 1000 : null,
      jitterBufferDelayMs,
      fractionLost,
      packetsReceived,
      transport,
      usingRelay,
    };
  }
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
